export interface DCFAssumptions {
  normalizedOwnerEarnings: number;      // in millions
  year1to5GrowthRate: number;           // decimal e.g. 0.08
  year6to10GrowthRate: number;          // decimal e.g. 0.04
  terminalGrowthRate: number;           // decimal e.g. 0.02
  wacc: number;                         // decimal e.g. 0.09
  terminalMultiple: number;             // e.g. 15
  netCash: number;                      // in millions
  cashHaircutPercent: number;           // decimal e.g. 0.15
  sharesOutstanding: number;            // UNITS: millions of shares
                                        // e.g. Apple = 15080, not 15,080,000,000
                                        // The engine multiplies by 1,000,000
                                        // internally for the cross-check.
                                        // Passing actual share count (not millions)
                                        // will cause the cross-check to fail.
  useTerminalMultiple: boolean;         // true = multiple method, false = Gordon Growth
  currentPrice?: number;                // current share price for implied growth calculation
  isType4Financial?: boolean;           // true for banks, insurers, insurance conglomerates
  projectedYear10TBVTotal?: number;     // total projected Year 10 TBV in MILLIONS (not per share)
  emergencyFloorApplied?: boolean;      // flag for Oracle to detect distressed inputs
  cashFlowBasis?: 'FCFF' | 'OwnerEarnings'; // Levered vs Unlevered routing
  ticker?: string;                      // For logging
  type4TerminalRescaled?: boolean;      // Result measurement flag
  terminalMultipleOriginal?: number | null; // Original P/TBV multiple before rescale
}

const DCF_VERBOSE = process.env.DCF_VERBOSE === 'true';

/**
 * Structural constraints enforced on every scenario set.
 * These are passed alongside assumptions to validate that
 * bear/bull cases are structurally distinct, not just
 * arithmetic haircuts of the base.
 */
export interface ScenarioConstraints {
  // Bear case: multiple must be at least this far below base
  bearMultipleFloorDiscountPct: number;   // e.g. 0.35 = bear multiple must be ≤ base × (1 - 0.35)
  // Bear case: Y1-5 growth must be at least this far below base
  bearGrowthCeilingDiscountPct: number;   // e.g. 0.40 = bear growth must be ≤ base × (1 - 0.40)
  // Bull case: multiple must be at least this far above base
  bullMultipleFloorPremiumPct: number;    // e.g. 0.15 = bull multiple must be ≥ base × (1 + 0.15)
  // Bull case: Y1-5 growth must be at least this far above base
  bullGrowthFloorPremiumPct: number;      // e.g. 0.20 = bull growth must be ≥ base × (1 + 0.20)
}

/**
 * Default constraints. These are the minimum structural separations
 * required between scenarios. Gemini may pass tighter separations
 * (more extreme bear/bull) but never looser ones.
 */
export const DEFAULT_SCENARIO_CONSTRAINTS: ScenarioConstraints = {
  bearMultipleFloorDiscountPct: 0.35,
  bearGrowthCeilingDiscountPct: 0.40,
  bullMultipleFloorPremiumPct: 0.15,
  bullGrowthFloorPremiumPct: 0.20,
};

export interface DCFValuation {
  projectedOwnerEarnings: number[];     // 10 years, in millions
  presentValues: number[];              // 10 years, in millions
  sumOfPVs: number;                     // in millions
  terminalValue: number;                // in millions
  pvOfTerminalValue: number;            // in millions
  adjustedNetCash: number;              // after haircut, millions
  totalEquityValue: number;             // in millions
  sharesOutstanding: number;            // from input
  intrinsicValuePerShare: number;       // exact division result
  impliedMarketGrowth: number;          // back-calculated
  crossCheckPassed: boolean;            // IV * shares = equity?
  crossCheckDelta: number;              // delta in millions
  emergencyFloorApplied: boolean;
  cashFlowBasis?: 'FCFF' | 'OwnerEarnings';
  type4TerminalRescaled?: boolean;
  terminalMultipleOriginal?: number | null;
}

function runDCFInternal(assumptions: DCFAssumptions, skipImpliedGrowth: boolean): DCFValuation {
  // DCF INPUT VALIDATION GATE
  // A negative base owner earnings figure fed into a growth-rate DCF
  // produces a sign-flipping cascade that renders all outputs meaningless.
  // This gate catches the error before it propagates.
  if (assumptions.normalizedOwnerEarnings <= 0) {
    console.error(
      `[DCF ENGINE] FATAL INPUT ERROR: normalizedOwnerEarnings=` +
      `${assumptions.normalizedOwnerEarnings}. Negative or zero base ` +
      `will produce oscillating projections. Applying emergency floor.`
    );
    
    // Apply emergency floor — use absolute value of FCF * 0.1 or 1M
    const marketCapImpliedFloor =
      assumptions.currentPrice && assumptions.currentPrice > 0 &&
      assumptions.sharesOutstanding && assumptions.sharesOutstanding > 0
        ? assumptions.currentPrice * assumptions.sharesOutstanding * 0.005
        : 1;
    const emergencyFloor = Math.max(
      Math.abs(assumptions.normalizedOwnerEarnings) * 0.1,
      1,
      marketCapImpliedFloor
    );
    
    assumptions = {
      ...assumptions,
      normalizedOwnerEarnings: emergencyFloor,
      emergencyFloorApplied: true
    };
  }

  // Confirm structural decline pattern is valid (positive base + negative growth)
  if (assumptions.year1to5GrowthRate < 0 && assumptions.normalizedOwnerEarnings > 0) {
    if (DCF_VERBOSE) {
      console.log(
        `[DCF ENGINE] Structural decline pattern confirmed valid: ` +
        `positive base (${assumptions.normalizedOwnerEarnings.toFixed(1)}M) ` +
        `+ negative growth (${(assumptions.year1to5GrowthRate * 100).toFixed(1)}%). ` +
        `Clean downward slope will be produced.`
      );
    }
  }

  const projectedOwnerEarnings: number[] = [];
  const presentValues: number[] = [];
  
  let currentEarnings = assumptions.normalizedOwnerEarnings;
  let sumOfPVs = 0;
  
  for (let year = 1; year <= 10; year++) {
    const growthRate = year <= 5 ? assumptions.year1to5GrowthRate : assumptions.year6to10GrowthRate;
    currentEarnings = currentEarnings * (1 + growthRate);
    projectedOwnerEarnings.push(currentEarnings);
    
    const pv = currentEarnings / Math.pow(1 + assumptions.wacc, year);
    presentValues.push(pv);
    sumOfPVs += pv;
  }
  
  const year10Earnings = projectedOwnerEarnings[9];
  
  const marketCapEstimate = assumptions.sharesOutstanding * (assumptions.currentPrice || 0);
  const terminalGrowthCap = marketCapEstimate > 100000 ? 0.025 : 0.035;
  assumptions.terminalGrowthRate = Math.min(assumptions.terminalGrowthRate, terminalGrowthCap);

  let terminalValue = 0;
  if (assumptions.isType4Financial && assumptions.projectedYear10TBVTotal != null && assumptions.projectedYear10TBVTotal > 0) {
    // Type 4: terminal value = P/TBV multiple × projected Year 10 total TBV (in millions)
    // terminalMultiple here IS the P/TBV multiple (e.g., 1.4 for 1.4x book)
    terminalValue = assumptions.terminalMultiple * assumptions.projectedYear10TBVTotal;
  } else if (assumptions.useTerminalMultiple) {
    let earningsTerminalMultiple = assumptions.terminalMultiple;
    if (assumptions.isType4Financial && (!assumptions.projectedYear10TBVTotal || assumptions.projectedYear10TBVTotal <= 0)) {
      if (assumptions.terminalMultiple < 5.0) {
        earningsTerminalMultiple = Math.min(1 / (assumptions.wacc - assumptions.terminalGrowthRate), 15.0);
        assumptions.type4TerminalRescaled = true;
        assumptions.terminalMultipleOriginal = assumptions.terminalMultiple;
        // Per-cell log gated behind DCF_VERBOSE (Tuning v2.66 §41.3): runDCF is called once per
        // sensitivity-grid cell, so this fired ~561x on BRK.B (the §19.2 DCF-spam class). The fired
        // STATE is already captured deterministically on [DCF-OUTPUT-TRACE] and the
        // type4TerminalRescaled / terminalMultipleOriginal props — the per-cell line is scratch.
        if (DCF_VERBOSE) {
          console.log(`[TYPE4-TERMINAL-RESCALE] ${assumptions.ticker || 'UNKNOWN'}: TBV-null fallback received terminalMultiple=${assumptions.terminalMultiple.toFixed(1)}x (P/TBV-scale) — rescaled to ${earningsTerminalMultiple.toFixed(1)}x earnings-scale (1/(WACC−g), cap 15.0).`);
        }
      }
    }
    terminalValue = year10Earnings * earningsTerminalMultiple;
  } else {
    terminalValue = year10Earnings * (1 + assumptions.terminalGrowthRate) / (assumptions.wacc - assumptions.terminalGrowthRate);
  }
  
  const pvOfTerminalValue = terminalValue / Math.pow(1 + assumptions.wacc, 10);
  
  const adjustedNetCash = assumptions.netCash > 0 
    ? assumptions.netCash * (1 - assumptions.cashHaircutPercent)
    : assumptions.netCash;
  
  if (DCF_VERBOSE) {
    console.log(`[DCF-BRIDGE] cashFlowBasis=${assumptions.cashFlowBasis || 'OwnerEarnings'} | sumOfPVs=${sumOfPVs} | pvOfTV=${pvOfTerminalValue} | netDebt=${-adjustedNetCash}`);
  }
  
  let totalEquityValue: number;
  if (assumptions.cashFlowBasis === 'FCFF') {
    // FCFF discounts to Enterprise Value — net debt deduction is correct
    totalEquityValue = sumOfPVs + pvOfTerminalValue + adjustedNetCash;
  } else {
    // Owner Earnings discounts to Equity Value directly — net debt already embedded in cash flows
    // We still add positive net cash, but floor the debt deduction at zero.
    totalEquityValue = sumOfPVs + pvOfTerminalValue + Math.max(0, adjustedNetCash);
  }
  
  // Floor at zero — equity cannot be negative in this model
  totalEquityValue = Math.max(0, totalEquityValue);
  
  const intrinsicValuePerShare = totalEquityValue / assumptions.sharesOutstanding;
  
  // Cross-check: convert both sides to actual dollars.
  // totalEquityValue is in millions → multiply by 1,000,000.
  // sharesOutstanding is in millions → multiply by 1,000,000.
  // intrinsicValuePerShare is already in actual dollars (M / M = $).
  // If sharesOutstanding was passed as actual count rather than
  // millions, this check will fail with a massive delta — by design.
  const actualEquityValueRaw = totalEquityValue * 1_000_000;
  const recalculatedEquityValue = intrinsicValuePerShare * (assumptions.sharesOutstanding * 1_000_000);
  const crossCheckDelta = Math.abs(actualEquityValueRaw - recalculatedEquityValue) / 1_000_000;
  const crossCheckPassed = (crossCheckDelta / totalEquityValue) <= 0.005;

  let impliedMarketGrowth = -1;
  if (!skipImpliedGrowth && assumptions.currentPrice !== undefined && assumptions.currentPrice > 0) {
    let low = -0.99; // -99% growth
    let high = 5.0;  // 500% growth
    
    for (let i = 0; i < 50; i++) {
      let mid = (low + high) / 2;
      let testAssumptions = {
        ...assumptions,
        year1to5GrowthRate: mid,
        year6to10GrowthRate: mid
      };
      let testVal = runDCFInternal(testAssumptions, true);
      if (testVal.intrinsicValuePerShare > assumptions.currentPrice) {
        high = mid;
      } else {
        low = mid;
      }
    }
    impliedMarketGrowth = (low + high) / 2;
  }

  return {
    projectedOwnerEarnings,
    presentValues,
    sumOfPVs,
    terminalValue,
    pvOfTerminalValue,
    adjustedNetCash,
    totalEquityValue,
    sharesOutstanding: assumptions.sharesOutstanding,
    intrinsicValuePerShare,
    impliedMarketGrowth,
    crossCheckPassed,
    crossCheckDelta,
    emergencyFloorApplied: assumptions.emergencyFloorApplied || false,
    cashFlowBasis: assumptions.cashFlowBasis || 'OwnerEarnings',
    type4TerminalRescaled: assumptions.type4TerminalRescaled,
    terminalMultipleOriginal: assumptions.terminalMultipleOriginal
  };
}

export function runDCF(assumptions: DCFAssumptions): DCFValuation {
  return runDCFInternal(assumptions, false);
}

export function runScenarios(
  base: DCFAssumptions,
  bearOverrides: Partial<DCFAssumptions>,
  bullOverrides: Partial<DCFAssumptions>
): { bear: DCFValuation; base: DCFValuation; bull: DCFValuation } {
  return {
    bear: runDCF({ ...base, ...bearOverrides }),
    base: runDCF(base),
    bull: runDCF({ ...base, ...bullOverrides })
  };
}

/**
 * Validates that the proposed bear/bull overrides satisfy the
 * structural separation constraints. Returns a list of violations.
 * An empty array means the scenarios passed validation.
 */
export function validateScenarioConstraints(
  base: DCFAssumptions,
  bearOverrides: Partial<DCFAssumptions>,
  bullOverrides: Partial<DCFAssumptions>,
  constraints: ScenarioConstraints = DEFAULT_SCENARIO_CONSTRAINTS
): string[] {
  const violations: string[] = [];

  const bearGrowth = bearOverrides.year1to5GrowthRate ?? base.year1to5GrowthRate;
  const bearMultiple = bearOverrides.terminalMultiple ?? base.terminalMultiple;
  const bullGrowth = bullOverrides.year1to5GrowthRate ?? base.year1to5GrowthRate;
  const bullMultiple = bullOverrides.terminalMultiple ?? base.terminalMultiple;

  const maxBearGrowth = base.year1to5GrowthRate * (1 - constraints.bearGrowthCeilingDiscountPct);
  const maxBearMultiple = base.terminalMultiple * (1 - constraints.bearMultipleFloorDiscountPct);
  const minBullGrowth = base.year1to5GrowthRate * (1 + constraints.bullGrowthFloorPremiumPct);
  const minBullMultiple = base.terminalMultiple * (1 + constraints.bullMultipleFloorPremiumPct);

  if (bearGrowth > maxBearGrowth) {
    violations.push(
      `Bear Y1-5 growth (${(bearGrowth*100).toFixed(1)}%) is too close to base ` +
      `(${(base.year1to5GrowthRate*100).toFixed(1)}%). ` +
      `Must be ≤ ${(maxBearGrowth*100).toFixed(1)}% to reflect structural stress.`
    );
  }

  if (bearMultiple > maxBearMultiple) {
    violations.push(
      `Bear terminal multiple (${bearMultiple.toFixed(1)}x) is too close to base ` +
      `(${base.terminalMultiple.toFixed(1)}x). ` +
      `Must be ≤ ${maxBearMultiple.toFixed(1)}x to reflect multiple contraction.`
    );
  }

  if (bullGrowth < minBullGrowth) {
    violations.push(
      `Bull Y1-5 growth (${(bullGrowth*100).toFixed(1)}%) is not sufficiently above base ` +
      `(${(base.year1to5GrowthRate*100).toFixed(1)}%). ` +
      `Must be ≥ ${(minBullGrowth*100).toFixed(1)}% to represent a genuine upside case.`
    );
  }

  if (bullMultiple < minBullMultiple) {
    violations.push(
      `Bull terminal multiple (${bullMultiple.toFixed(1)}x) is not sufficiently above base ` +
      `(${base.terminalMultiple.toFixed(1)}x). ` +
      `Must be ≥ ${minBullMultiple.toFixed(1)}x to represent a genuine upside case.`
    );
  }

  // BULL-3a: Detect narratively thin bull cases for software businesses
  // This does not block generation but flags for Oracle disclosure
  const bullMultiplePremium = bullMultiple / base.terminalMultiple;
  const bullGrowthPremium = bullGrowth / base.year1to5GrowthRate;
  if (
    base.terminalMultiple > 20 &&
    bullMultiplePremium < 1.20 &&
    bullGrowthPremium < 1.25
  ) {
    violations.push(
      `BULL-3a Advisory: Bull multiple premium (${((bullMultiplePremium - 1) * 100).toFixed(1)}%) and growth premium (${((bullGrowthPremium - 1) * 100).toFixed(1)}%) are both near constraint floors for a high-multiple business. Bull case may lack structural differentiation from base. Consider whether a named peer anchor justifies a wider separation.`
    );
  }

  return violations;
}

export function runSensitivities(base: DCFAssumptions) {
  const baseIV = runDCF({ ...base }).intrinsicValuePerShare;
  
  const waccPlus1 = runDCF({ ...base, wacc: base.wacc + 0.01 }).intrinsicValuePerShare;
  const waccMinus1 = runDCF({ ...base, wacc: base.wacc - 0.01 }).intrinsicValuePerShare;
  
  const growthPlus2 = runDCF({ ...base, year1to5GrowthRate: base.year1to5GrowthRate + 0.02 }).intrinsicValuePerShare;
  const growthMinus2 = runDCF({ ...base, year1to5GrowthRate: base.year1to5GrowthRate - 0.02 }).intrinsicValuePerShare;

  const ivDeltaPerWaccPlus1 = waccPlus1 - baseIV;
  const ivDeltaPerGrowthPlus2 = growthPlus2 - baseIV;

  return {
    baseIV,
    ivWaccPlus1: waccPlus1,
    ivWaccMinus1: waccMinus1,
    ivGrowthPlus2: growthPlus2,
    ivGrowthMinus2: growthMinus2,
    ivDeltaPerWaccPlus1,
    ivDeltaPerGrowthPlus2,
    growthFieldUsed: 'year1to5GrowthRate' as const
  };
}
